import { A } from '@adornis/base/env-info.js';
import { dataViewTranslations } from '@adornis/data-view/default-translations.js';
import { fieldManagementTranslations } from '@adornis/field-management/default-translations.js';
import { filterTranslations } from '@adornis/filter/default-translations.js';
import { tiptapTranslations } from '@adornis/tiptap/translations.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import { ExamDayRatingType, ExamType } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';

export const brandDictionary = {
  ...dataViewTranslations,
  ...fieldManagementTranslations,
  ...filterTranslations,
  ...tiptapTranslations,

  // global content
  osce: 'OSCE',
  osces: 'OSCEs',
  'osce-group': 'OSCE-Gruppe',
  'osce-groups': 'OSCE-Gruppen',
  product_type: 'Prüfung',

  invalid_username: 'Nutzername oder Passwort falsch',
  no_password: 'Kein Passwort eingegeben',

  // page specific content
  page: {
    product: {
      title: 'Prüfungsanmeldung',
      text_1: 'Bitte wählen Sie einen der unten angegebenen Termine für Ihre Prüfung aus.',
    },
    signup: {
      title: 'Prüfungstermin buchen',
    },
    scheduling: {
      title: 'Stundenplan',
    },
    check_in: {
      title: 'Check-In zur Prüfung',
    },
    spectate: {
      title: 'Zuschauen',
    },
    exam: {
      title: 'Termin',
    },
    exam_trainee: {
      title: 'Termin',
    },
    tester_dashboard: {
      title: 'Dashboard',
    },
    evaluation: {
      title: 'Evaluation',
    },
    users: {
      title: 'Nutzerverwaltung',
    },
    locations: {
      title: 'Standortverwaltung',
    },
    soundcheck: {
      title: 'Soundcheck',
    },
    start: {
      title: 'Start',
    },
    osce_editor: {
      title: 'OSCE-Editor',
    },
    osce_editor_group: {
      title: 'OSCE-Gruppen-Editor',
    },
    exam_editor: {
      title: 'Prüfungstag-Editor',
    },
    mails: {
      title: 'Mails',
    },
    evaluation_overview: {
      title: 'Bewertungen',
    },
    evaluation_timeallocation: {
      title: 'Terminfindung',
    },
    evaluation_async: {
      title: 'Bewertung',
    },
    evaluation_sync: {
      title: 'Bewertung',
    },
    evaluation_termin: {
      title: 'Bewertung',
    },
    local_recordings: {
      title: 'Lokale Aufnahmen',
    },
    content_osce: {
      title: 'OSCEs ansehen',
    },
    preparation: {
      title: 'Vorbereitungsmaterialien',
    },
    candidate_dashboard: {
      title: 'Dashboard',
    },
    book_appointments: {
      title: 'Termine buchen',
    },
    export: {
      title: 'Daten exportieren',
    },
    reported_errors: {
      title: 'Gemeldete Fehler',
    },
    tester_book_appointments: {
      title: 'Termine buchen',
    },
  },
  field: {
    _id: 'eindeutige ID',
    _class: 'eindeutiger Typ',
    username: 'Nutzername',
    isOnline: 'Gerade Online',
    ios_sub: 'iOS ID',
    emailConfirmed: 'E-Mail bestätigt',
    coInvoice: 'E-Mail CC',
    email: 'E-Mail Adresse',
    firstName: 'Vorname',
    lastName: 'Nachname',
    fullName: 'Vollname',
    osce: 'OSCE',
    ratingDoc: 'Bewertung',
    ratingDuration: 'Bewertungsdauer',
    slot: 'Prüfungssituation',
    inspectors: 'Bewerter',
    trainee: 'Prüfling',
    ecommerce: 'Nutzerdaten',
    orders: 'Bestellungen',
    createdAt: 'Erstellt am',
    updatedAt: 'Aktualisiert am',
    revokedAt: 'Reklamiert am',
    startTime: 'Startzeit',
    committed: 'Bestätigt',
    examType: 'Prüfungstyp',
    linguistCommitted: 'Mediziner bestätigt',
    doctorCommitted: 'Sprachwissenschaftler bestätigt',
    archived: 'Archiviert',
    ratings: 'Einzelwertungen',
    score: 'Wertung',
    notes: 'Notizen',
    title: 'Titel',
    titleTrainee: 'Titel für Prüflinge',
    osceType: 'OSCE-Typ',
    ratingConfigName: 'Bewertungs Konfiguration',
    durationInMinutes: 'Dauer in Minuten',
    enableVideo: 'Videochat aktiviert',
    taskType: 'Aufgabentyp',
    conversationType: 'Konversationstyp',
    street: 'Straße',
    zip: 'PLZ',
    city: 'Stadt',
    country: 'Land',
    phone: 'Telefonnummer',
    acceptedLegal: 'Datenschutzerklärung akzeptiert',
    wantsNewsletter: 'Bekommt Newsletter',
    acceptedTerms: 'AGB akzeptiert',
    profile: 'Profil',
    birthplace: 'Geburtsort',
    birthCountry: 'Geburtsland',
    gender: 'Geschlecht',
    status: 'Bezahlstatus',
    products: 'Produkte',
    product: 'Produkt',
    name: 'Name',
    location: 'Standort',
    displayName: 'Anzeigename',
    address: 'Adresse',
    streetAndHousenumber: 'Straße und Hausnummer',
    zipCity: 'PLZ und Stadt',
    bundesland: 'Bundesland',
    additionalInfo: 'Zusätzliche Informationen',
    description: 'Beschreibung',
    activeReservationsCount: 'Anzahl aktive Reservierungen',
    published: 'Veröffentlicht',
    infoText: 'Informationstext',
    start: 'Start',
    end: 'Ende',
    hasBreak: 'Hat Pause',
    breakAfterRounds: 'Pause nach Runden',
    breakInMinutes: 'Pausenlänge in Minuten',
    slotPause: 'Pause zwischen den Slots',
    blockPause: 'Pause zwischen den Blöcken',
    amount: 'Preis (in Cent)',
    paymentType: 'Zahlungsmethode',
    tax: 'Umsatzsteuer',
    invoiceNumber: 'Rechnungsnummer',
    orderNumber: 'Bestellnummer',
    comment: 'Kommentar',
    dateOfBirth: 'Geburtsdatum',
    user: 'Nutzer',
    passedExamDay: 'Prüfung bestanden',
    examday: 'Prüfungstag',
    order: 'Bestellung',
    userScore: 'Nutzerbewertung',
    isRated: 'Fertig bewertet?',
    stripePaymentIntent: 'Stripe ID',
    processorInfo: 'Stripe Info',
    targetFederalState: 'Bundesland',
    hasParticipatedExam: 'Bereits einmal teilgenommen (Nutzerangabe)',
    participatedC1Course: 'Bereits C1 Kurs belegt (Nutzerangabe)',
    type: 'Typ',
    ratingType: 'Bewertungstyp',
    isPublishedForTesters: 'Veröffentlicht (für Schauspieler)',

    value: {
      [Names.ExamSlotRatingDoc._class]: 'Asynchone Bewertung',
      [Names.ExamSlotFinalRatingDoc._class]: 'Synchrone Bewertung',
    },
  },

  ratingType: {
    [ExamDayRatingType.BAYERN]: { short: 'FaMed-Bayern', long: 'Bayern' },
    [ExamDayRatingType.RLP]: {
      short: 'FaMed-RLP',
      long: 'Rheinland-Pfalz',
    },
    [ExamDayRatingType.FAMED]: {
      short: 'FaMed',
      long: 'FaMed',
    },
    other: { short: 'Andere', long: 'Andere' },
  },
  examType: {
    [ExamType.FAMED]: { short: 'FaMed', long: 'Fachsprachenprüfung Medizin' },
    [ExamType.FSPP]: {
      short: 'FSPP',
      long: 'Fachsprachenprüfung Pflege',
    },
    other: { short: 'Andere', long: 'Andere' },
  },

  osce_editor: {
    page_title: [],
    editor_title: ['OSCE bearbeiten', 'Inhalt bearbeiten'],
    usages: ['Prüfungstage in denen dieses OSCE verwendet wird', 'Prüfungstage in denen dieser Inhalt verwendet wird'],
    video_upload_error: ['Error beim Video-Upload für das Osce', 'Error beim Video-Upload für den Inhalt'],
  },

  error: {
    no_access: 'Sie haben hierfür keine Berechtigung',
  },

  order_status: {
    active: 'aktiv',
    created: 'erstellt',
    issued: 'ausgestellt',
    canceled: 'abgebrochen',
    expired: 'abgelaufen',
    incomplete: 'unvollständig',
    incomplete_expired: 'unvollständig abgelaufen',
    past_due: 'überfällig',
    trialing: 'Probeabo',
    unpaid: 'unbezahlt',
    paused: 'pausiert',
    paid: 'bezahlt',
    revoked: 'storniert',
    refund: 'erstattet',
    failed: 'fehlgeschlagen',
    moved: 'verschoben',
  },

  payment_method: {
    acss_debit: 'ACSS-Lastschrift',
    affirm: 'Affirm',
    afterpay_clearpay: 'Afterpay/Clearpay',
    alipay: 'Alipay',
    au_becs_debit: 'AU BECS-Lastschrift',
    bacs_debit: 'BACS-Lastschrift',
    bancontact: 'Bancontact',
    blik: 'Blik',
    boleto: 'Boleto',
    card: 'Kredit-/Debitkarte',
    cashapp: 'Cash App',
    customer_balance: 'Kundenkontostand',
    eps: 'EPS',
    fpx: 'FPX (Malaysia)',
    giropay: 'Giropay',
    grabpay: 'GrabPay',
    ideal: 'iDEAL',
    klarna: 'Klarna',
    konbini: 'Konbini',
    link: 'Link',
    oxxo: 'OXXO',
    p24: 'Przelewy24 (P24)',
    paynow: 'PayNow',
    paypal: 'PayPal',
    pix: 'Pix',
    promptpay: 'PromptPay',
    sepa_debit: 'SEPA-Lastschrift',
    sofort: 'SOFORT-Überweisung',
    us_bank_account: 'US-Bankkonto',
    wechat_pay: 'WeChat Pay',
    zip: 'Zip',
  },
};

export type brandDict = typeof brandDictionary;

if (A.isClient) {
  clientTranslator.updateDictionary(brandDictionary, [Language.GERMAN]);

  globalLanguageSubject.next(Language.GERMAN);
}
